import React, { useState, useRef } from "react";
import styled from "styled-components";
import Button from "components/Button";
import { ReactComponent as LoadIcon } from "assets/load.svg";
import { uploadImage } from "api";

const StyledUploadButton = styled.div`
  .label {
    margin-bottom: 12px;
    font-size: 12px;
    font-weight: 600;
  }
  .preview {
    display: block;
    border-radius: 3px;
    max-height: 200px;
    max-width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.lightGrayBorder};
  }
`;

const Alert = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.4;
  border-radius: ${({ theme }) => theme.standardRadius};
  color: ${({ theme }) => theme.colors.darkText};
  background-color: ${({ theme, variant }) =>
    variant === "error" ? theme.colors.lightDanger : theme.colors.lightInfo};
  border: 1px solid
    ${({ theme, variant }) =>
      variant === "error" ? theme.colors.danger : theme.colors.info};
`;

const readImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };
    img.src = url;
  });

const UploadButton = ({
  onClick,
  readOnly,
  label,
  onUpload,
  url,
  jwtToken,
  validation,
  helperText,
  ...rest
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const inputRef = useRef();

  const handleButtonClick = () => {
    inputRef.current.click();
  };

  const validateFile = async (file) => {
    if (!validation) return true;

    const {
      maxSizeKB,
      allowedMimeTypes,
      maxWidth,
      maxHeight,
      requiredRatio,
      ratioTolerance = 0.05,
    } = validation;

    if (maxSizeKB && file.size / 1024 > maxSizeKB) {
      setErrorMessage(`L'image doit être inférieure à ${maxSizeKB}KB.`);
      return false;
    }

    if (
      allowedMimeTypes &&
      allowedMimeTypes.length &&
      !allowedMimeTypes.includes(file.type)
    ) {
      setErrorMessage("Format d'image non valide.");
      return false;
    }

    if (maxWidth || maxHeight || requiredRatio) {
      try {
        const { width, height } = await readImageDimensions(file);

        if (maxWidth && width > maxWidth) {
          setErrorMessage(`La largeur doit être ≤ ${maxWidth}px.`);
          return false;
        }

        if (maxHeight && height > maxHeight) {
          setErrorMessage(`La hauteur doit être ≤ ${maxHeight}px.`);
          return false;
        }

        if (requiredRatio) {
          const ratio = width / height;
          const diff = Math.abs(ratio - requiredRatio) / requiredRatio;
          if (diff > ratioTolerance) {
            setErrorMessage("Le ratio de l'image n'est pas correct.");
            return false;
          }
        }
      } catch (error) {
        setErrorMessage("Impossible de lire les dimensions de l'image.");
        return false;
      }
    }

    setErrorMessage(null);
    return true;
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const isValid = await validateFile(file);
    if (!isValid) {
      setIsUploading(false);
      return;
    }

    uploadImage(file, jwtToken)
      .then((uploadUrl) => {
        setIsUploading(false);
        if (uploadUrl) {
          onUpload(uploadUrl);
        } else {
          setErrorMessage("Upload échoué: aucune URL retournée.");
        }
      })
      .catch((err) => {
        console.error("error uploading", err);
        setErrorMessage(
          err?.message || "Erreur pendant l'upload, veuillez réessayer."
        );
        setIsUploading(false);
      });
  };

  return (
    <StyledUploadButton {...rest}>
      {label && <div className="label">{label}</div>}
      {url && (
        <a target="__blank" href={url} onClick={onClick}>
          <img className="preview" src={url} alt="preview upload" />
        </a>
      )}
      <Button
        type="file"
        secondary
        disabled={isUploading || readOnly}
        onClick={handleButtonClick}
        style={
          url
            ? {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              }
            : {}
        }
      >
        {isUploading ? "En cours ..." : url ? "Changer d'image" : "Upload"}
        {isUploading && (
          <LoadIcon
            height="1.6em"
            style={{ animation: "rotate 1s infinite linear", marginLeft: 16 }}
          />
        )}
        <input
          hidden
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          ref={inputRef}
        />
      </Button>
      <Button
        danger
        onClick={() => {
          inputRef.current.value = "";
          onUpload(undefined);
        }}
        disabled={!url || readOnly}
      >
        Effacer
      </Button>
      {helperText ? <Alert>{helperText}</Alert> : null}
      {errorMessage ? <Alert variant="error">{errorMessage}</Alert> : null}
    </StyledUploadButton>
  );
};

export default UploadButton;
